"use client";
import { motion, Variants } from "framer-motion";
import { Zap } from "lucide-react";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import { TeamQA } from "@/lib/types";

interface TeamRapidFireProps {
  qa: TeamQA[];
}

const card: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

// Quick-hit personality: a grid of one-line question/answer cards driven by the
// member's `qa`. Rendered only when the data exists.
const TeamRapidFire = ({ qa }: TeamRapidFireProps) => {
  return (
    <section className="bg-background py-14 md:py-20">
      <Container className="px-4 lg:px-10">
        <div className="space-y-3">
          <SectionSubHeading subtitle="Rapid Fire" className="text-primary" />
          <SectionTitle title={<>QUICK &mdash; DON&rsquo;T THINK.</>} />
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 md:mt-20">
          {qa.map((item, i) => (
            <motion.div
              key={item.q}
              variants={card}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
              className="rounded-2xl border-2 border-black bg-secondary p-6 shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-transform duration-300 hover:-translate-y-1"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border-2 border-black bg-primary">
                  <Zap className="h-4 w-4" />
                </span>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                  {item.q}
                </p>
              </div>
              <p className="mt-4 font-black leading-snug tracking-tight text-xl md:text-2xl">
                {item.a}
              </p>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default TeamRapidFire;
